// frontend/src/pages/AnalysisPage/AnalysisPage.js
// Purpose: Main container for analysis workflow
// Desired outcome: Create a central hub for initiating new analyses and viewing reports with proper step tracking and navigation

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectStep, 
  selectProgress, 
  selectReports,
  selectError,
  selectReportData,
  fetchReports,
  clearAnalysis,
  clearError,
  setStep
} from '../../state/analysis/analysisSlice';
import AnalysisNavigation from '../../components/AnalysisNavigation/AnalysisNavigation';
import ReportList from '../../components/ReportList/ReportList';
import logger from '../../utils/logger';
import './AnalysisPage.css';

// Import section components
import FileSelection from './sections/FileSelection';
import ColumnMapping from './sections/ColumnMapping/ColumnMapping';
import PartMatching from './sections/PartMatching/PartMatching';
import PartContext from './sections/PartContext/PartContext';
import Calculations from './sections/Calculations/Calculations';
import ValueSavingsAnalysis from './sections/ValueSavingsAnalysis/ValueSavingsAnalysis';
import ReportGeneration from './sections/ReportGeneration/ReportGeneration';

const AnalysisPage = () => {
  const dispatch = useDispatch();
  const currentStep = useSelector(selectStep);
  const progress = useSelector(selectProgress);
  const reports = useSelector(selectReports);
  const error = useSelector(selectError);
  const reportData = useSelector(selectReportData);
  
  // Local state for UI
  const [showReports, setShowReports] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use refs to track initialization state to prevent repeated fetches
  const initialFetchDone = useRef(false);
  const startingNewAnalysis = useRef(false);

  // Fetch reports only on initial mount
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;  // Set immediately to prevent multiple calls
      
      dispatch(fetchReports())
        .unwrap()
        .then(() => {
          setIsLoading(false);
          logger.info('Reports loaded successfully');
        })
        .catch(err => {
          setIsLoading(false);
          logger.error('Error loading reports', err);
        });
    }
  }, [dispatch]);

  // Show report generation view if a report is loaded
  useEffect(() => {
    if (reportData && reportData.reportID) {
      setShowReports(false);
      // Set the step to report generation to show the report details
      dispatch(setStep('reportGeneration'));
    }
  }, [dispatch, reportData]);

  // Start a new analysis
  const startNewAnalysis = () => {
    // Prevent multiple clicks or state changes
    if (startingNewAnalysis.current) return;
    startingNewAnalysis.current = true;
    
    logger.info('Starting new analysis workflow');
    
    // First change the view state
    setShowReports(false);
    
    // Then dispatch Redux actions, but only once
    dispatch(clearAnalysis());
    
    // Reset the ref after 500ms to allow for future clicks
    setTimeout(() => {
      startingNewAnalysis.current = false;
    }, 500);
  };

  // Return to reports view
  const viewReports = () => {
    // Prevent changing state during transitions
    if (progress.status === 'loading') return;
    
    setShowReports(true);
    logger.info('Returning to reports view');
  };

  // Handle error dismissal
  const handleDismissError = () => {
    dispatch(clearError());
  };

  // Create a memoized version of the current step component to prevent unnecessary re-renders
  const CurrentStepComponent = useCallback(() => {
    switch (currentStep) {
      case 'fileSelection':
        return <FileSelection />;
      case 'columnMapping':
        return <ColumnMapping />;
      case 'partMatching':
        return <PartMatching />;
      case 'partContext':
        return <PartContext />;
      case 'calculations':
        return <Calculations />;
      case 'valueSavingsAnalysis':
        return <ValueSavingsAnalysis />;
      case 'reportGeneration':
        return <ReportGeneration />;
      default:
        return <div>Unknown step</div>;
    }
  }, [currentStep]);

  return (
    <div className="analysis-page">
      <div className="analysis-header">
        <h1>Analysis Dashboard</h1>
        <div className="action-buttons">
          {showReports ? (
            <button 
              className="primary-button" 
              onClick={startNewAnalysis}
              disabled={startingNewAnalysis.current}
            >
              Start New Analysis
            </button>
          ) : (
            <button 
              className="secondary-button" 
              onClick={viewReports}
              disabled={progress.status === 'loading'}
            >
              View Reports
            </button>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="alert alert-error">
          <p>{error.message || 'An error occurred'}</p>
          <button className="dismiss-button" onClick={handleDismissError}>
            Dismiss
          </button>
        </div>
      )}

      {/* Main content area */}
      <div className="analysis-content">
        {showReports ? (
          // Reports view
          <div className="reports-container">
            <h2>Previous Reports</h2>
            <ReportList 
              reports={reports} 
              isLoading={isLoading || progress.status === 'loading'} 
            />
          </div>
        ) : (
          // Analysis workflow view
          <div className="workflow-container">
            <AnalysisNavigation currentStep={currentStep} />
            <div className="step-content">
              {progress.status === 'loading' ? (
                <div className="loading-indicator">
                  <p>Loading...</p>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${progress.percentComplete}%` }}
                    ></div>
                  </div>
                </div>
              ) : (
                <CurrentStepComponent />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisPage;